import { ApiError } from "./client.js";
import { fetchEveryPage } from "./collection.js";
import { parseFeedList } from "./parse.js";
import type { FeedSummary } from "./types.js";

/**
 * Which feeds exist, and what they are called.
 *
 * The published seam does not answer this. `GET /api/feeds/{id}/now-playing` is
 * open to anyone but carries only `feed_id`; `GET /api/feeds` carries the name and
 * requires `Authorization: Bearer …` (`apps/api/src/openapi.ts` — `FeedListRoute`).
 * So an anonymous listener can be told everything about a feed except that it
 * exists and what it is called.
 *
 * Until the API publishes feed identity, this module is the only place in the app
 * that knows a feed's name without being told by the API. It asks the API first and
 * takes its answer whenever it gets one; it falls back to a configured list, whose
 * default names today's on-air feed, as Icecast announces it. Live data —
 * state, track, artwork, deliveries — always comes from the public per-feed route,
 * for configured and API-listed feeds alike.
 *
 * STOP-NOTE: closing this gap is an `apps/api` change (a public projection of
 * `{id, name, description}` for enabled feeds, or a `name` on the public
 * now-playing response) and is deliberately not made here.
 */

export type FeedDirectorySource = "api" | "configured";

export interface FeedDirectory {
  feeds: FeedSummary[];
  source: FeedDirectorySource;
  /** Why the API's list was not used, when it was not. Shown in Settings, not on the listening surface. */
  gap: string | null;
}

const FALLBACK_FEEDS: FeedSummary[] = [
  {
    id: "af3a2075-9a4d-42d1-a4fb-013e17c7b2c1",
    name: "Primary feed",
    description: "",
  },
];

/**
 * `VITE_RADIO_FEEDS` is a JSON array of `{id, name, description?}`. It exists so a
 * deployment can name its feeds without a code change while the API's list is
 * closed; a malformed value is ignored rather than allowed to empty the surface.
 */
export function configuredFeeds(raw: string | undefined): FeedSummary[] {
  if (!raw || raw.trim() === "") return FALLBACK_FEEDS;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return FALLBACK_FEEDS;
    const feeds = parsed
      .map((entry): FeedSummary | null => {
        if (typeof entry !== "object" || entry === null) return null;
        const { id, name, description } = entry as Record<string, unknown>;
        if (typeof id !== "string" || id === "") return null;
        return {
          id,
          name: typeof name === "string" && name !== "" ? name : "Feed",
          description: typeof description === "string" ? description : "",
        };
      })
      .filter((feed) => feed !== null);
    return feeds.length > 0 ? feeds : FALLBACK_FEEDS;
  } catch {
    return FALLBACK_FEEDS;
  }
}

export async function loadFeedDirectory(
  signal?: AbortSignal,
): Promise<FeedDirectory> {
  const configured = configuredFeeds(import.meta.env.VITE_RADIO_FEEDS);
  try {
    const feeds = await fetchEveryPage("/api/feeds", parseFeedList, { signal });
    if (feeds.length > 0) return { feeds, source: "api", gap: null };
    return {
      feeds: configured,
      source: "configured",
      gap: "Feed list empty.",
    };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError")
      throw error;
    return {
      feeds: configured,
      source: "configured",
      gap: describeGap(error),
    };
  }
}

function describeGap(error: unknown): string {
  if (
    error instanceof ApiError &&
    (error.status === 401 || error.status === 403)
  ) {
    return "Feed list unavailable.";
  }
  if (error instanceof ApiError && error.status > 0) {
    return "Feed list unavailable.";
  }
  return "Feed list unavailable.";
}
